import React, { useState, useCallback, useRef, useEffect, useContext, createContext } from 'react';
import { convsAPI, msgsAPI, weighmentAPI } from '../utils/api';
import { useAuth } from './AuthContext';

const ChatContext = React.createContext(null);

export function ChatProvider({ children }) {
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvIdState] = useState(null);
  const [messages, setMessagesState] = useState({});
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('chats');
  const [weighmentUnread, setWeighmentUnread] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);

  const { user } = useAuth();

  // Refs so polling callbacks never capture stale state
  const messagesRef = useRef({});
  const lastPollRef = useRef({});
  const activeConvIdRef = useRef(null);

  // User-specific polling state
  const getStorageKey = useCallback((key) => user ? `${key}_${user.id}` : key, [user]);
  const lastWeighmentIdRef = useRef(0);
  const processedWeighmentIds = useRef(new Set());

  // Reset polling state whenever user changes (account switch)
  useEffect(() => {
    if (user) {
      const key = getStorageKey('last_weighment_id');
      lastWeighmentIdRef.current = parseInt(localStorage.getItem(key) || '0');
      processedWeighmentIds.current = new Set();
      setMessagesState({});
      setConversations([]);
      messagesRef.current = {};
      lastPollRef.current = {};
    } else {
      lastWeighmentIdRef.current = 0;
      processedWeighmentIds.current = new Set();
    }
  }, [user, getStorageKey]);

  const setMessages = useCallback((updater) => {
    setMessagesState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      messagesRef.current = next;
      return next;
    });
  }, []);

  const setActiveConvId = useCallback((id) => {
    activeConvIdRef.current = id;
    setActiveConvIdState(id);
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const res = await convsAPI.list();
      if (res.data.success) setConversations(res.data.data.conversations);
    } catch { }
  }, []);

  const loadArchivedConversations = useCallback(async () => {
    try {
      const res = await convsAPI.list(1);
      return res.data.success ? res.data.data.conversations : [];
    } catch { return []; }
  }, []);

  const openConversation = useCallback(async (convId) => {
    setActiveConvId(convId);
    if (!messagesRef.current[convId]) {
      setLoading(true);
      try {
        const res = await msgsAPI.list(convId);
        if (res.data.success) {
          setMessages(prev => ({ ...prev, [convId]: res.data.data.messages }));
        }
      } catch { }
      setLoading(false);
    }
    convsAPI.markRead(convId).catch(() => { });
    setConversations(prev => prev.map(c => c.id === convId ? { ...c, unread_count: 0 } : c));
  }, [setActiveConvId, setMessages]);

  const sendMessage = useCallback(async (convId, data) => {
    try {
      const res = await msgsAPI.send({ conversation_id: convId, ...data });
      if (res.data.success) {
        const msg = res.data.data.message;
        setMessages(prev => ({ ...prev, [convId]: [...(prev[convId] || []), msg] }));
        setConversations(prev => {
          const updated = prev.map(c => c.id === convId
            ? { ...c, last_message: msg.content || `[${msg.type}]`, last_message_at: msg.created_at, unread_count: 0 }
            : c);
          return updated.sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
        });
        return msg;
      }
    } catch { }
  }, [setMessages]);

  // STABLE: uses refs only — interval never needs to be recreated
  const pollMessages = useCallback(async (convId, isActiveConv = false) => {
    const msgs = messagesRef.current[convId] || [];
    const lastId = msgs.length ? msgs[msgs.length - 1].id : 0;
    const updatedSince = lastPollRef.current[convId];
    try {
      const res = await msgsAPI.poll(convId, lastId, updatedSince);
      if (!res.data.success) return;

      const newMsgs = res.data.data.messages || [];
      const updMsgs = res.data.data.updated_messages || [];
      lastPollRef.current[convId] = new Date().toISOString();

      if (newMsgs.length > 0) {
        setMessages(prev => {
          const existing = (prev[convId] || []).filter(m => !m.is_optimistic);
          return {
            ...prev,
            [convId]: [...existing, ...newMsgs],
          };
        });
        setConversations(prev => {
          const last = newMsgs[newMsgs.length - 1];
          return prev.map(c => c.id === convId
            ? {
              ...c,
              last_message: last.content || `[${last.type}]`,
              last_message_at: last.created_at,
              unread_count: isActiveConv ? 0 : (c.unread_count || 0) + newMsgs.length,
            }
            : c
          ).sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
        });
        if (isActiveConv) convsAPI.markRead(convId).catch(() => { });
      }

      if (updMsgs.length > 0) {
        setMessages(prev => {
          const existing = [...(prev[convId] || [])];
          updMsgs.forEach(upd => {
            const idx = existing.findIndex(m => m.id === upd.id);
            if (idx !== -1) existing[idx] = { ...existing[idx], ...upd };
          });
          return { ...prev, [convId]: existing };
        });
      }
    } catch { }
  }, [setMessages]); // setMessages is stable

  const deleteMessage = useCallback(async (convId, msgId) => {
    await msgsAPI.delete(msgId);
    setMessages(prev => ({
      ...prev,
      [convId]: prev[convId].map(m => m.id === msgId
        ? { ...m, is_deleted: 1, content: 'This message was deleted' } : m),
    }));
  }, [setMessages]);

  const editMessage = useCallback(async (convId, msgId, content) => {
    const res = await msgsAPI.edit(msgId, content);
    if (res.data.success) {
      const updated = res.data.data.message;
      setMessages(prev => ({
        ...prev,
        [convId]: prev[convId].map(m => m.id === msgId ? { ...m, ...updated } : m),
      }));
    }
  }, [setMessages]);

  const reactMessage = useCallback(async (convId, msgId, emoji) => {
    const res = await msgsAPI.react(msgId, emoji);
    if (res.data.success) {
      const reactions = res.data.data.reactions;
      setMessages(prev => ({
        ...prev,
        [convId]: prev[convId].map(m => m.id === msgId ? { ...m, reactions } : m),
      }));
    }
  }, [setMessages]);

  const unreactMessage = useCallback(async (convId, msgId) => {
    const res = await msgsAPI.unreact(msgId);
    if (res.data.success) {
      const reactions = res.data.data.reactions;
      setMessages(prev => ({
        ...prev,
        [convId]: prev[convId].map(m => m.id === msgId ? { ...m, reactions } : m),
      }));
    }
  }, [setMessages]);

  const startDirectChat = useCallback(async (userId) => {
    const res = await convsAPI.direct(userId);
    if (res.data.success) {
      await loadConversations();
      openConversation(res.data.data.conversation_id);
    }
  }, [loadConversations, openConversation]);

  const appendMessage = useCallback((convId, msg) => {
    setMessages(prev => {
      const existing = prev[convId] || [];
      if (existing.find(m => m.id === msg.id)) return prev;
      return { ...prev, [convId]: [...existing, msg] };
    });
  }, [setMessages]);

  // ── Real-time Polling Logic ──────────────────────────────────────────────

  // 1. Global Heartbeat: Sync conversation list & unread counts (every 3s)
  useEffect(() => {
    let timer;
    const pollConversations = async () => {
      try {
        await loadConversations();
      } catch { }
      timer = setTimeout(pollConversations, 3000);
    };
    pollConversations();
    return () => clearTimeout(timer);
  }, [loadConversations]);

  // 1.1 Call Polling (every 3s)
  useEffect(() => {
    let timer;
    const pollCalls = async () => {
      try {
        const res = await callsAPI.poll();
        if (res.data.success && res.data.data.call) {
          setIncomingCall(res.data.data.call);
        }
      } catch { }
      timer = setTimeout(pollCalls, 3000);
    };
    pollCalls();
    return () => clearTimeout(timer);
  }, []);

  // 2. Active Chat Polling: Faster updates for the open conversation (every 1.5s)
  useEffect(() => {
    let timer;
    const doActivePoll = async () => {
      if (activeConvIdRef.current) {
        try {
          await pollMessages(activeConvIdRef.current, true);
        } catch { }
      }
      timer = setTimeout(doActivePoll, 1500);
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        clearTimeout(timer);
        doActivePoll();
        loadConversations();
        // Force refresh for other tabs
        window.dispatchEvent(new CustomEvent('app:weighment_refresh'));
        window.dispatchEvent(new CustomEvent('app:bill_refresh'));
      }
    };

    doActivePoll();
    window.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  }, [pollMessages, loadConversations]);

  // 3. Weighment & System Events Polling (every 2s)
  useEffect(() => {
    let timer;
    const POLL_INTERVAL = 1500; // Faster for "instant" feel

    const playNotificationSound = () => {
      try {
        const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3');
        audio.volume = 0.4;
        audio.play().catch(() => { });
        if (navigator.vibrate) navigator.vibrate([80, 40, 80]);
      } catch (e) { }
    };

    const doWeighmentPoll = async () => {
      try {
        const key = getStorageKey('last_weighment_id');
        const storedId = parseInt(localStorage.getItem(key) || '0');
        if (storedId > lastWeighmentIdRef.current) {
          lastWeighmentIdRef.current = storedId;
        }

        const res = await weighmentAPI.poll(lastWeighmentIdRef.current);
        if (res.data.success && res.data.events && res.data.events.length) {
          const events = res.data.events;
          const isFirstLoad = lastWeighmentIdRef.current === 0;

          // Sort events by ID to ensure sequential processing
          events.sort((a, b) => a.id - b.id);
          const maxId = events[events.length - 1].id;

          lastWeighmentIdRef.current = maxId;
          localStorage.setItem(key, maxId.toString());

          if (isFirstLoad) return;

          let hasNewImportant = false;

          events.forEach(ev => {
            if (processedWeighmentIds.current.has(ev.id)) return;
            processedWeighmentIds.current.add(ev.id);

            const p = ev.payload || {};

            // 1. Build a clean message string (Absolute safety against 'undefined' or 'undefind')
            const sender = String(p.sender_name || p.party_name || p.customer_name || 'System').replace(/undefin/gi, 'System');
            const vehicle = String(p.vehicle_no || p.vehicle || 'N/A').replace(/undefin/gi, 'N/A');
            const material = String(p.material || p.item || 'Unknown').replace(/undefin/gi, 'Unknown');
            const ticket = String(p.ticket_no || p.id || 'N/A').replace(/undefin/gi, 'N/A');
            const netWeight = p.net_weight || p.net || '0';

            let rawMsg = p.data || p.message || '';
            const needsRebuild = !rawMsg ||
              String(rawMsg).toLowerCase().includes('undefin') ||
              String(rawMsg).toLowerCase().includes('[object');

            let safeMsg = rawMsg;
            if (needsRebuild) {
              if (ev.event_type === 'bill_created') {
                const ref = String(p.bill_reference || p.reference || 'N/A').replace(/undefin/gi, 'N/A');
                const amount = p.total_amount || p.amount || '0';
                safeMsg = `New Bill ${ref}: Amount ₹${amount}`;
              } else {
                safeMsg = `${sender} sent Weighment #${ticket}: ${material} — Vehicle ${vehicle} (${netWeight} kg)`;
              }
            }

            // Final sanitize: No 'undefin' allowed in UI
            safeMsg = String(safeMsg).replace(/undefin/gi, 'N/A');

            // 2. Dispatch events for UI tabs
            if (ev.event_type === 'weighment_created') {
              hasNewImportant = true;
              window.dispatchEvent(new CustomEvent('weighment:new', { detail: p }));

              const isActive = (p.conversation_id && parseInt(p.conversation_id) === parseInt(activeConvIdRef.current));

              // Only notify if not actively looking at this specific chat
              if (!isActive) {
                window.dispatchEvent(new CustomEvent('app:notification', {
                  detail: { ...p, title: '⚖️ New Weighment', type: 'success', message: safeMsg }
                }));

                // Native Notification for Mobile
                if (window.Notification && window.Notification.permission === 'granted') {
                  new window.Notification('⚖️ New Weighment', { body: safeMsg, data: p });
                }
              }
            } else if (ev.event_type === 'bill_created') {
              hasNewImportant = true;
              window.dispatchEvent(new CustomEvent('weighment:bill_new', { detail: p }));

              const isActive = (p.conversation_id && parseInt(p.conversation_id) === parseInt(activeConvIdRef.current));

              if (!isActive) {
                window.dispatchEvent(new CustomEvent('app:notification', {
                  detail: { ...p, title: '🧾 New Bill', type: 'info', message: safeMsg }
                }));

                // Native Notification for Mobile
                if (window.Notification && window.Notification.permission === 'granted') {
                  new window.Notification('🧾 New Bill', { body: safeMsg, data: p });
                }
              }
            }

            // 3. INSTANT CHAT & LIST UPDATE:
            if (p.conversation_id) {
              const convId = parseInt(p.conversation_id);
              const isActive = (convId === parseInt(activeConvIdRef.current));

              // A. Inject into Message List (if active)
              if (isActive) {
                const msgObj = {
                  id: `tmp_ev_${ev.id}`,
                  conversation_id: convId,
                  sender_id: p.sender_id || 0,
                  sender_name: p.sender_name || 'System',
                  content: safeMsg,
                  type: 'text',
                  created_at: p.created_at || new Date().toISOString(),
                  is_system: true,
                  is_optimistic: true // Mark for later cleanup
                };
                appendMessage(convId, msgObj);
              }

              // B. Update Conversation List Preview & Unread Count
              setConversations(prev => {
                const updated = prev.map(c => {
                  if (c.id === convId) {
                    return {
                      ...c,
                      last_message: safeMsg,
                      last_message_at: p.created_at || new Date().toISOString(),
                      unread_count: isActive ? 0 : (parseInt(c.unread_count) || 0) + 1,
                    };
                  }
                  return c;
                });
                // Sort by time
                return updated.sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
              });
            }
          });

          if (hasNewImportant) {
            setWeighmentUnread(true);
            playNotificationSound();
            // loadConversations(); // No longer strictly needed for instant feel, but good for final sync
          }
        }
      } catch (err) { }
      timer = setTimeout(doWeighmentPoll, POLL_INTERVAL);
    };

    doWeighmentPoll();
    return () => clearTimeout(timer);
  }, [loadConversations, appendMessage, getStorageKey]);

  return (
    <ChatContext.Provider value={{
      conversations, setConversations,
      activeConvId, setActiveConvId,
      messages, setMessages,
      loading, tab, setTab,
      weighmentUnread, setWeighmentUnread,
      incomingCall, setIncomingCall,
      loadConversations, loadArchivedConversations,
      openConversation, sendMessage, pollMessages,
      deleteMessage, editMessage, reactMessage, unreactMessage,
      startDirectChat, appendMessage,
    }}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  return useContext(ChatContext);
}
